import React, { useEffect, useState } from 'react';
import { ExternalLink, ArrowRight, Loader2, CheckCircle2, Clock, XCircle, ShieldAlert } from 'lucide-react';
import API from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const StatusPill = ({ status }) => {
  const map = {
    PENDING: { bg: 'bg-amber-100 text-amber-900 border-amber-500/40', label: 'Pending', Icon: Clock },
    APPROVED: { bg: 'bg-emerald-100 text-emerald-900 border-emerald-600/40', label: 'Successfully Transferred', Icon: CheckCircle2 },
    REJECTED: { bg: 'bg-red-100 text-red-900 border-red-500/40', label: 'Rejected', Icon: XCircle },
  };
  const s = map[status] || map.PENDING;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${s.bg}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
};

const ShareTransfer = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [eligible, setEligible] = useState(0);
  const [qty, setQty] = useState('');
  const [apexEmail, setApexEmail] = useState('');
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [e, h] = await Promise.all([
        API.get('/share-transfers/eligible-shares'),
        API.get('/share-transfers/me'),
      ]);
      setEligible(e.data?.eligible_shares || 0);
      setHistory(h.data?.items || []);
    } catch {}
  };
  useEffect(() => {
    load();
    if (user?.email) setApexEmail(user.email);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    const q = parseInt(qty, 10);
    if (!q || q <= 0) return toast({ title: 'Enter shares quantity', description: 'Please enter a valid number of shares.' });
    setSubmitting(true);
    try {
      const { data } = await API.post('/share-transfers', { shares_qty: q, apex_email: apexEmail });
      toast({
        title: 'Transfer request submitted',
        description: data?.message || 'Your shares will be transferred to apexforge.io within 7-15 days.',
        duration: 9000,
      });
      setQty('');
      load();
    } catch (err) {
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const kycApproved = user?.kyc_status === 'APPROVED';

  return (
    <div className="max-w-[1240px] mx-auto pt-4 pb-16 px-4 md:px-6">
      {/* Prominent red dividend warning */}
      <div
        data-testid="share-transfer-dividend-warning"
        className="mb-4 rounded-2xl border-2 border-red-500 bg-red-50 px-5 py-4 flex items-start gap-3 shadow-[0_2px_20px_rgba(220,38,38,0.15)]"
      >
        <ShieldAlert size={22} className="text-red-600 shrink-0 mt-0.5" />
        <div className="text-[13.5px] text-red-800 leading-relaxed font-medium">
          <span className="font-bold text-red-700">Important:</span> If you transfer these shares to ApexForge.io through Share Exchange, <b>you will no longer receive dividends</b> on the transferred shares. Their dividend accrual will stop from the moment your transfer is approved. Only the shares that remain with SkyWay Green will continue earning dividends.
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-to-b from-[#fdf9e7] to-[#f7f1cf] border border-emerald-900/15 p-6 md:p-10 shadow-[0_4px_40px_rgba(4,45,25,0.08)]">
        <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-700 font-semibold">Share Transfer Setup</div>
        <h1 className="mt-1 font-serif text-emerald-950 text-[26px] md:text-[32px] leading-tight">
          Move your SkyWay Green shares to <span className="text-amber-600">ApexForge.io</span>
        </h1>
        <p className="mt-2 text-[13.5px] text-emerald-950/70 max-w-3xl">
          Only shares from <b>fully-paid</b> packages (all installments completed) are eligible. Complete registration and KYC on ApexForge.io using the <b>same email</b> and the <b>same KYC document</b> as your SkyWay Green account.
        </p>

        {/* ApexForge link box */}
        <a
          href="https://apexforge.io/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="apexforge-link"
          className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-amber-500/60 bg-white hover:bg-amber-50 transition px-5 py-4 group"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Register & Complete KYC</div>
            <div className="mt-1 text-emerald-950 font-semibold text-[16px]">https://apexforge.io/</div>
            <div className="text-[12px] text-emerald-950/60">Click to open the ApexForge portal in a new tab</div>
          </div>
          <ExternalLink size={20} className="text-amber-600 group-hover:translate-x-0.5 transition" />
        </a>

        {/* Disclaimer */}
        <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-50 p-4 flex gap-3">
          <ShieldAlert size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-emerald-950/80 leading-relaxed">
            <b className="text-emerald-950">Disclaimer:</b> To be eligible for share transfer, you must register on ApexForge.io using the <b>same email address</b> and complete <b>the same KYC</b> as your SkyWay Green account. Mismatched details will delay or block your transfer.
          </div>
        </div>

        {/* Transfer form */}
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* From */}
          <div className="rounded-2xl border border-emerald-900/15 bg-white p-5">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">From</div>
            <div className="mt-1 font-serif text-emerald-950 text-[20px]">SkyWay Green</div>
            <div className="mt-3 text-[12px] text-emerald-950/60">Account: <span className="text-emerald-950 font-medium">{user?.email}</span></div>
          </div>
          {/* Arrow between From/To on desktop */}
          <div className="rounded-2xl border border-emerald-900/15 bg-white p-5 relative">
            <div className="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-emerald-950 text-emerald-50 items-center justify-center shadow-md">
              <ArrowRight size={16} />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">To</div>
            <div className="mt-1 font-serif text-emerald-950 text-[20px]">ApexForge.io</div>
            <div className="mt-3 text-[12px] text-emerald-950/60">Target account email (auto-filled with your SkyWay email):</div>
            <input
              data-testid="apex-email-input"
              type="email"
              value={apexEmail}
              onChange={(e) => setApexEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full rounded-lg bg-[#fdf9e7] border border-emerald-900/15 focus:border-emerald-600 outline-none px-3 py-2 text-[13px] text-emerald-950"
            />
          </div>

          {/* Share Details */}
          <div className="rounded-2xl border border-emerald-900/15 bg-white p-5 md:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Share Details</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-900/60">Company</div>
                <div className="mt-1 font-serif text-emerald-950 text-[18px]">SkyWay Green</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-900/60">Eligible Shares</div>
                <div data-testid="eligible-shares-count" className="mt-1 font-serif text-emerald-950 text-[24px]">
                  {eligible.toLocaleString()}
                </div>
                <div className="text-[11px] text-emerald-900/60">from fully-paid packages</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-emerald-900/60">Shares to Transfer</label>
                <input
                  data-testid="shares-qty-input"
                  type="number"
                  min="1"
                  max={eligible}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder={eligible > 0 ? `1 – ${eligible}` : 'No eligible shares yet'}
                  disabled={eligible <= 0 || !kycApproved}
                  className="mt-1 w-full rounded-lg bg-[#fdf9e7] border border-emerald-900/15 focus:border-emerald-600 outline-none px-3 py-2 text-emerald-950 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {!kycApproved && (
            <div className="md:col-span-2 rounded-xl border border-red-500/40 bg-red-50 p-4 text-[13px] text-red-900">
              You must complete KYC verification before submitting a share transfer request.
            </div>
          )}

          <button
            type="submit"
            data-testid="submit-share-transfer-btn"
            disabled={submitting || eligible <= 0 || !kycApproved}
            className="md:col-span-2 w-full rounded-xl bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 uppercase tracking-widest text-sm transition inline-flex items-center justify-center gap-2"
          >
            {submitting ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : 'Submit Share Transfer Request'}
          </button>
        </form>

        {/* History */}
        <div className="mt-10">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-3">Transfer History</div>
          <div className="rounded-2xl border border-emerald-900/15 bg-white divide-y divide-emerald-900/10 overflow-hidden">
            {history.length === 0 && (
              <div className="p-6 text-[13px] text-emerald-950/60 text-center">No share transfer requests yet.</div>
            )}
            {history.map((t) => (
              <div key={t.id} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`share-transfer-row-${t.id}`}>
                <div>
                  <div className="text-emerald-950 font-medium text-[14px]">{t.shares_qty} share{t.shares_qty === 1 ? '' : 's'} · SkyWay Green → ApexForge.io</div>
                  <div className="text-[11.5px] text-emerald-900/60">Requested {new Date(t.created_at).toLocaleString()}{t.approved_at ? ` · Approved ${new Date(t.approved_at).toLocaleString()}` : ''}</div>
                  {t.status === 'REJECTED' && t.reason && (
                    <div className="text-[12px] text-red-700 mt-1">Reason: {t.reason}</div>
                  )}
                </div>
                <StatusPill status={t.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareTransfer;
